const TAG_NAME = 'upd-focus-out';

const template = document.createElement('template');
template.innerHTML = `
  <style>
    :host {
      display: block;
    }
  </style>

  <slot></slot>
`;

const HAS_SHADY_CSS = window.hasOwnProperty('ShadyCSS');
if (HAS_SHADY_CSS) {
  window.ShadyCSS.prepareTemplate(template, TAG_NAME);
}

class FocusOut extends HTMLElement {
  static get observedAttributes() {
    // Using `is-disabled` since IE11 will restyle inputs/buttons
    // when an ancestor is `disabled`. If we ever drop IE11 support,
    // we might want to revisit the naming here to align with native
    // `disabled` attribute/properties
    return ['is-disabled'];
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
  }

  connectedCallback() {
    // Shim Shadow DOM styles. Needs to be run in connectedCallback.
    // Needed for IE 11.
    if (HAS_SHADY_CSS) {
      window.ShadyCSS.styleElement(this);
    }

    this._addListener();
  }

  attributeChangedCallback(name) {
    if (name === 'is-disabled') {
      this.isDisabled ? this._removeListener() : this._addListener();
    }
  }

  set isDisabled(value) {
    value
      ? this.setAttribute('is-disabled', '')
      : this.removeAttribute('is-disabled');
  }

  get isDisabled() {
    return this.hasAttribute('is-disabled');
  }

  _addListener() {
    this.addEventListener('focusout', this._handleFocusOut);
  }

  _removeListener() {
    this.removeEventListener('focusout', this._handleFocusOut);
  }

  _handleFocusOut = (event) => {
    // Need setTimeout or else the document.activeElement will be incorrect.
    setTimeout(() => {
      if (!isActiveElementInside(this, deepActiveElement())) {
        this.dispatchEvent(
          new CustomEvent('updfocusout', {
            bubbles: true,
            composed: true,
          })
        );
      }
    }, 0);
  };
}

window.customElements.define(TAG_NAME, FocusOut);

// Walk backwards up the DOM from the activeElement to
// either the focus-out component or the root to determine
// if the activeElement is inside focus-out still.
function isActiveElementInside(element, activeElement) {
  let a = activeElement;
  while (element !== a && (a.assignedSlot || a.parentNode || a.host)) {
    a = a.assignedSlot || a.parentNode || a.host;
  }
  return element === a;
}

// Due to how Shadow DOM currently works, we have to walk
// down all shadowRoots to get the actual activeElement
function deepActiveElement() {
  let a = document.activeElement;
  while (a && a.shadowRoot && a.shadowRoot.activeElement) {
    a = a.shadowRoot.activeElement;
  }
  return a;
}
